// MARK://dhk8/admin-shell/components/Skeleton
// Bloque de carga con shimmer (vía @keyframes en su propio <style>). `Skeleton`
// es un rectángulo configurable; `SkeletonText` apila N líneas (la última más corta,
// como un párrafo real). Tokens del sistema, sin colores hardcodeados.
import type { CSSProperties } from 'react'

export interface SkeletonProps {
  /** Ancho CSS (number = px). Default 100%. */
  w?: number | string
  /** Alto CSS (number = px). Default 14px. */
  h?: number | string
  /** Radio CSS (number = px). Default --pos-radius-sm. */
  radius?: number | string
  style?: CSSProperties
  className?: string
}

function dim(v: number | string | undefined, fallback: string): string {
  if (v === undefined) return fallback
  return typeof v === 'number' ? `${v}px` : v
}

export default function Skeleton({ w, h, radius, style, className }: SkeletonProps) {
  return (
    <span
      className={`nui-skeleton${className ? ` ${className}` : ''}`}
      aria-hidden
      style={{
        width: dim(w, '100%'),
        height: dim(h, '14px'),
        borderRadius: dim(radius, 'var(--pos-radius-sm, 6px)'),
        ...style,
      }}
    >
      <SkeletonStyle />
    </span>
  )
}

export interface SkeletonTextProps {
  /** Número de líneas. Default 3. */
  lines?: number
}

export function SkeletonText({ lines = 3 }: SkeletonTextProps) {
  return (
    <span className="nui-skeleton-text" role="status" aria-busy="true" aria-label="Cargando…">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} h={12} w={i === lines - 1 && lines > 1 ? '60%' : '100%'} />
      ))}
      <SkeletonStyle />
    </span>
  )
}

// El bloque de estilos se incluye una sola vez por instancia (CSS idempotente:
// repetir la misma regla no causa daño y mantiene el componente self-contained).
function SkeletonStyle() {
  return (
    <style>{`
      .nui-skeleton {
        display: block;
        position: relative;
        overflow: hidden;
        background: var(--pos-elevated);
        flex-shrink: 0;
      }
      .nui-skeleton::after {
        content: "";
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(
          90deg,
          transparent,
          color-mix(in oklch, var(--pos-fg) 8%, transparent),
          transparent
        );
        animation: nui-skeleton-shimmer 1.4s var(--pos-ease-in-out, ease-in-out) infinite;
      }
      .nui-skeleton-text {
        display: flex;
        flex-direction: column;
        gap: var(--pos-space-2, 8px);
        width: 100%;
      }
      @keyframes nui-skeleton-shimmer {
        100% { transform: translateX(100%); }
      }
      @media (prefers-reduced-motion: reduce) {
        .nui-skeleton::after { animation: none; }
      }
    `}</style>
  )
}
